import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Gift, Plus, Clock, Edit, Power, Upload, X, Image } from 'lucide-react';
import { promotionApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

export default function AdminPromotions() {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [bannerPreview, setBannerPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    banner: '',
    reward_amount: 0,
    requirements: {
      direct_referrals: 0,
      level2: 0,
      level3: 0
    },
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await promotionApi.getAll();
      setPromotions(response.data.promotions);
    } catch (error) {
      console.error('Error fetching promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      setFormData({
        name: promo.name,
        banner: promo.banner || '',
        reward_amount: promo.reward_amount,
        requirements: promo.requirements || { direct_referrals: 0, level2: 0, level3: 0 },
        start_date: promo.start_date?.split('T')[0] || '',
        end_date: promo.end_date?.split('T')[0] || ''
      });
      setBannerPreview(promo.banner || null);
    } else {
      setEditingPromo(null);
      setFormData({
        name: '',
        banner: '',
        reward_amount: 0,
        requirements: { direct_referrals: 0, level2: 0, level3: 0 },
        start_date: '',
        end_date: ''
      });
      setBannerPreview(null);
    }
    setShowModal(true);
  };

  const handleBannerSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Banner image must be less than 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setBannerPreview(base64);
        setFormData({...formData, banner: base64});
      };
      reader.readAsDataURL(file);
    }
  };

  const removeBanner = () => {
    setBannerPreview(null);
    setFormData({...formData, banner: ''});
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.start_date || !formData.end_date) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      if (editingPromo) {
        await promotionApi.update(editingPromo.id, formData);
        toast.success('Promotion updated');
      } else {
        await promotionApi.create(formData);
        toast.success('Promotion created');
      }
      setShowModal(false);
      setEditingPromo(null);
      fetchPromotions();
    } catch (error) {
      toast.error(editingPromo ? 'Failed to update promotion' : 'Failed to create promotion');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (promoId) => {
    try {
      await promotionApi.toggle(promoId);
      toast.success('Promotion status updated');
      fetchPromotions();
    } catch (error) {
      toast.error('Failed to update promotion');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-promotions">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-pink-500/10">
            <Gift className="w-6 h-6 text-pink-400" />
          </div>
          <div>
            <h1 className="text-2xl text-white font-medium">Promotions</h1>
            <p className="text-sm text-zinc-500">Create and manage achievement rewards</p>
          </div>
        </div>

        <Button onClick={() => handleOpenModal()} className="gold-btn" data-testid="create-promotion-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Promotion
        </Button>
      </div>

      {/* Promotions Grid */}
      {promotions.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Gift className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl text-white mb-2">No Promotions</h3>
          <p className="text-zinc-500">Create your first promotion to reward users</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {promotions.map((promo, idx) => (
            <motion.div
              key={promo.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className={`glass-card overflow-hidden ${promo.is_active === false ? 'opacity-60' : ''}`}
            >
              {/* Banner Preview */}
              {promo.banner ? (
                <div className="h-24 overflow-hidden">
                  <img 
                    src={promo.banner} 
                    alt={promo.name} 
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="h-16 bg-gradient-to-r from-pink-500/20 to-purple-500/20 flex items-center justify-center">
                  <Image className="w-6 h-6 text-zinc-500" />
                </div>
              )}
              
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-white font-medium">{promo.name}</h3>
                    <p className="text-amber-400 text-2xl font-bold">${promo.reward_amount}</p>
                  </div>
                  <span className={`badge ${promo.is_active !== false ? 'badge-success' : 'badge-danger'}`}>
                    {promo.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </div>

              <div className="space-y-2 text-sm mb-4">
                {Object.entries(promo.requirements || {}).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-zinc-500 capitalize">{key.replace('_', ' ')}</span>
                    <span className="text-white">{value}</span>
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-white/10 flex items-center gap-2 text-xs text-zinc-500 mb-4">
                <Clock className="w-3 h-3" />
                {new Date(promo.start_date).toLocaleDateString()} - {new Date(promo.end_date).toLocaleDateString()}
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleOpenModal(promo)}
                  className="flex-1 border-white/10"
                >
                  <Edit className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleToggle(promo.id)}
                  className={`flex-1 ${promo.is_active !== false ? 'border-red-500/30 text-red-400' : 'border-green-500/30 text-green-400'}`}
                >
                  <Power className="w-3 h-3 mr-1" /> {promo.is_active !== false ? 'Disable' : 'Enable'}
                </Button>
              </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">{editingPromo ? 'Edit Promotion' : 'Create Promotion'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Promotion Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., New Year Bonus"
                className="bg-black/50 border-white/10"
              />
            </div>

            {/* Banner Upload */}
            <div>
              <label className="text-xs text-zinc-400 mb-2 block">
                Promotion Banner (Optional - shown on user dashboard)
              </label>
              {bannerPreview ? (
                <div className="relative">
                  <img 
                    src={bannerPreview} 
                    alt="Banner preview" 
                    className="w-full h-32 object-cover rounded-lg border border-white/10"
                  />
                  <button
                    type="button"
                    onClick={removeBanner}
                    className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-white/10 rounded-lg p-4 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleBannerSelect}
                    className="hidden"
                  />
                  <Upload className="w-6 h-6 text-zinc-500 mx-auto mb-2" />
                  <p className="text-sm text-zinc-400">Click to upload banner</p>
                  <p className="text-xs text-zinc-500">PNG, JPG up to 5MB (Recommended: 800x200)</p>
                </div>
              )}
            </div>

            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Reward Amount ($)</label>
              <Input
                type="number"
                value={formData.reward_amount}
                onChange={(e) => setFormData({...formData, reward_amount: parseFloat(e.target.value)})}
                className="bg-black/50 border-white/10"
              />
            </div>

            <div>
              <label className="text-xs text-zinc-400 mb-2 block">Requirements</label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-zinc-500">Direct Refs</label>
                  <Input
                    type="number"
                    value={formData.requirements.direct_referrals}
                    onChange={(e) => setFormData({
                      ...formData, 
                      requirements: {...formData.requirements, direct_referrals: parseInt(e.target.value)}
                    })}
                    className="bg-black/50 border-white/10 h-8"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500">Level 2</label>
                  <Input
                    type="number"
                    value={formData.requirements.level2}
                    onChange={(e) => setFormData({
                      ...formData, 
                      requirements: {...formData.requirements, level2: parseInt(e.target.value)}
                    })}
                    className="bg-black/50 border-white/10 h-8"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500">Level 3</label>
                  <Input
                    type="number"
                    value={formData.requirements.level3}
                    onChange={(e) => setFormData({
                      ...formData, 
                      requirements: {...formData.requirements, level3: parseInt(e.target.value)}
                    })}
                    className="bg-black/50 border-white/10 h-8"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">Start Date</label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                  className="bg-black/50 border-white/10"
                />
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-2 block">End Date</label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  className="bg-black/50 border-white/10"
                />
              </div>
            </div>

            <Button onClick={handleSubmit} disabled={submitting} className="w-full gold-btn">
              {submitting ? (
                <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin" />
              ) : (
                editingPromo ? 'Update Promotion' : 'Create Promotion'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
